import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  NgModule,
  SimpleChanges,
} from '@angular/core';
import { Auth0Service } from 'src/app/auth/auth0.service';
import { Router, RouterModule } from '@angular/router';
import { Subject, Subscription, of, EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AppInfoService } from 'src/app/shared/services/app-info.service';
import { Landing } from 'src/app/shared/models/landing';
import { CommonModule } from '@angular/common';
import {
  DxButtonModule,
  DxPopupModule,
  DxTextBoxModule,
  DxToolbarModule,
  DxTabPanelModule
} from 'devextreme-angular';
import { HelpDropdownBtnModule } from './help-dropdown-btn/help-dropdown-btn.component';
import { UserPanelModule } from './user-panel/user-panel.component';
import { CartPanelModule } from './cart-panel/cart-panel.component';
import { UserService } from 'src/app/shared/services/user/user.service';
import { CartSideModule } from 'src/app/shared/components/cart-side/cart-side.component';
import { SwitchFacilitiesModule } from 'src/app/shared/components/switch-facilities/switch-facilities.component';
import { environment } from 'src/environments/environment';
import { AdminService } from 'src/app/shared/services/admin/admin.service';
import { SearchPanelModule } from './search-panel/search-panel.module';
import { DocumentUploadModule } from 'src/app/shared/components/document-upload/document-upload.component';
import { CalibrationFileUploadModule } from 'src/app/shared/components/calibration-file-upload/calibration-file-upload.module';
import { NotificationsModule } from 'src/app/shared/components/notifications/notifications.component';
import { JobTrackerIndicatorModule } from 'src/app/shared/components/job-tracker-indicator/job-tracker-indicator.component';
import { FacilityAndUserStatsModule } from 'src/app/shared/components/facility-and-user-stats/facility-and-user-stats.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { UserProfileModule } from 'src/app/shared/components/user-profile/user-profile.module';
import { FinancialStatementsUploaderModule } from 'src/app/shared/components/financial-statements-uploader/financial-statements-uploader.module';
import { ScreenService } from 'src/app/shared/services/screen.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { FeatureFlagsService } from 'src/app/shared/services/featureFlags.service';
import { AiAgentChatModule } from 'src/app/pages/admin/ai-agents/ai-agent-chat.module';

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() menuToggle = true;
  @Input() menuToggleEnabled = false;
  @Input() title: string;

  cartOpened: boolean;
  items_in_cart = 0;
  cart: any;
  roles: Array<string> = [];

  userMenuItems: Array<any> = [];
  isImpDataSub = false;

  isDarkMode = false;
  hasDarkMode = false;
  landingConfigs: Landing = null;

  isBuyer = false;
  isSeller = false;
  isAdmin = false;

  isImpersonating = false;
  auth0UserData: any;
  userId: number;
  isDocumentUploadOpen = false;
  documentUploadPopupTitle = 'Upload Your Documents';
  documentUploadPopupWidth = '600px';
  documentUploadFileType;
  facilityId;
  companyId;
  user;

  hydraEnabled = environment.hydraEnabled;
  isProfilePopupOpen: boolean = false;
  isSupportChatOpen: boolean = false;

  tabs: { id: number; title: string; template: string }[] = [
    { id: 1, title: 'State License', template: 'stateLicenseTemplate' },
    { id: 2, title: 'DEA License', template: 'deaLicenseTemplate' }
  ];

  largeLogo = '';
  smallLogo = '';
  logoAltText = '';
  supplierConfig = environment.supplierConfig;

  historyTab = { id: 3, title: 'Purchase History', template: 'purchaseHistoryTemplate' };

  // Unsubscribe so subscriptions won't fire after view is destroyed
  private onDestroy$: Subject<void> = new Subject<void>();
  subscriptions: Subscription = new Subscription();

  isSalesRepresentative = false;
  salesRepEmail: string | null = null;
  private lastSalesRepFacility: string | null = null;

  isSmall = false;
  isMedium = false;
  isLarge = false;

  constructor(
    public screen: ScreenService,
    public auth0: Auth0Service,
    public adminService: AdminService,
    public appService: AppInfoService,
    private userService: UserService,
    private sessionService: SessionService,
    private router: Router,
    private googleAnaylyticsService: GoogleAnalyticsService,
    private notificationService: NotificationService,
    private featureFlagsService: FeatureFlagsService
  ) {}

  monitorScreen() {
    this.setScreenStates();
    this.screen.changed.subscribe(() => this.setScreenStates());
  }

  setScreenStates() {
    this.isSmall =
      this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'];
    this.isMedium = this.screen.sizes['screen-medium'];
    this.isLarge =
      this.screen.sizes['screen-large'] || this.screen.sizes['screen-x-large'];
  }

  ngOnInit() {
    this.monitorScreen();
    this.getAppConfigs();
    this.setRolesAndMenu();
    this.impersonationCheck();
    this.watchPrimaryFacilityUpdatedTrigger();
    this.getNavLogo();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.menuToggle = this.menuToggle;
  }

  watchPrimaryFacilityUpdatedTrigger() {
    const s = this.sessionService.primaryFacilityUpdatedTrigger.subscribe(() =>
      this.setRolesAndMenu(true),
    );
    this.subscriptions.add(s);
  }

  getAppConfigs() {
    if (!this.hydraEnabled) {
      this.tabs.push(this.historyTab);
    }

    this.appService.get().subscribe(result => {
      this.landingConfigs = result;
      this.isDarkMode = result.theme.search('dark') !== -1;
    });
  }

  async setRolesAndMenu(refresh = false) {
    this.userService
      .getMe(refresh)
      .pipe(
        tap(user => {
          this.user = user.data;
          this.roles = user.data.roles;
          this.isBuyer = this.hasRole('Buyer');
          this.isSeller = this.hasRole('Seller');
          this.isAdmin = this.user.is_admin;
          this.isSalesRepresentative = this.user.is_sales_representative || this.user.is_customer_representative;
          this.facilityId = user.data.primary_facility_id;
          this.companyId = user.data.company_id;
          this.setUserMenu();
          this.checkExpiredLicenses();
        }),
        // Chained off getMe so the rep is fetched per header load, not per route.
        switchMap(user => this.salesRepFor(user.data)),
      )
      .subscribe(rep => {
        this.salesRepEmail = rep?.data?.sales_rep_email ?? null;
      });
  }

  /**
   * The server applies the flag and the buyer rules and returns a null email to
   * anyone else, so no gate is repeated here. Skipped unless the facility or
   * its zip changed, since most primaryFacilityUpdatedTrigger emitters change
   * neither.
   */
  private salesRepFor(user: any): Observable<any> {
    const facility = `${user.primary_facility_id}:${user.primary_facility?.delivery_address?.postal_code ?? ''}`;
    if (facility === this.lastSalesRepFacility) {
      return EMPTY;
    }
    this.lastSalesRepFacility = facility;

    return this.userService.getMySalesRep().pipe(
      catchError(() => {
        // The error interceptor rethrows a bare string, and a 401 through it
        // logs the user out. Clearing the key lets the next trigger retry.
        this.lastSalesRepFacility = null;
        return of(null);
      }),
    );
  }

  checkExpiredLicenses() {
    const facility = this.user?.primary_facility;
    if (!facility) {
      return;
    }

    if (facility.state_license_expired) {
      this.notificationService.showNotifyWithLink(
        'Your state license is expired! This will prevent you from placing any orders. Please click here to update, and our team will verify and reactivate this location.',
        ['/pharmacies'],
        'warning',
        30000,
      );
    }

    if (!facility.state_license_expired && facility.dea_license_expired) {
      if (this.featureFlagsService.get('warn_on_expired_dea')) {
        this.notificationService.showNotifyWithLink(
          'Your DEA License is Expired! This will prevent you from placing orders for controlled substances. Please click here to update your pharmacy.',
          ['/pharmacies'],
          'warning',
          15000,
        );
      }
    }
  }

  setUserMenu() {
    if (this.isBuyer) {
      this.userMenuItems = [
        {
          text: 'Profile',
          icon: 'profile',
          onClick: () => {
            this.isProfilePopupOpen = true;
          },
        },
        {
          text: 'Upload Your Documents',
          icon: 'file',
          onClick: () => {
            this.isDocumentUploadOpen = true;
          },
        },
        {
          text: 'Logout',
          icon: 'runner',
          onClick: () => {
            this.unimpersonateAndLogout();
          },
        },
      ];
    } else {
      this.userMenuItems = [
        {
          text: 'Profile',
          icon: 'profile',
          onClick: () => {
            this.isProfilePopupOpen = true;
          },
        },
        {
          text: 'Logout',
          icon: 'runner',
          onClick: () => {
            this.unimpersonateAndLogout();
          },
        },
      ];
    }
  }

  updateDocumentUploadPopup(fileType) {
    this.documentUploadPopupWidth = '800px';
    this.documentUploadFileType = fileType;
    if (fileType === 'SL') {
      this.documentUploadPopupTitle = 'Upload Your State License';
    } else if (fileType === 'DEA') {
      this.documentUploadPopupTitle = 'Upload Your DEA License';
    } else if (fileType === 'RX') {
      this.documentUploadPopupTitle = 'Upload Purchase History';
    }
  }

  unimpersonateAndLogout() {
    if (this.isImpersonating) {
      this.unimpersonate();
    }
    this.auth0.logout();
  }

  hasRole(role: string): boolean {
    if (this.roles && this.roles.length > 0) {
      return this.roles.includes(role);
    } else {
      return false;
    }
  }

  closeCar(event) {
    this.cartOpened = event;
  }

  logIn = () => {
    this.auth0.login();
  };

  switchTheme = () => {
    let theme = this.appService.theme;
    const hasDark = theme.search('dark') !== -1;
    const hasLight = theme.search('light') !== -1;

    if (hasDark) {
      this.setTheme(false, (theme = theme.replace('dark', 'light')));
    } else if (hasLight) {
      this.setTheme(true, theme.replace('light', 'dark'));
    } else {
      this.isDarkMode = false;
    }
  };

  setTheme(isDark: boolean, color: string) {
    this.isDarkMode = isDark;
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  goHome() {
    this.userService.getMe().subscribe(user => {
      if (user.data.roles.includes('Buyer')) {
        this.router.navigate(['/catalog']);
      }
      if (user.data.roles.includes('Seller')) {
        this.router.navigate(['/seller-home']);
      }
    });
  }


  impersonationCheck() {
    this.auth0.userProfile$.subscribe(result => {
      this.auth0UserData = result;

      // userProfile$ starts as null and getUser() yields undefined until the
      // Auth0 client has a user, so the first emission on /callback is empty.
      const meta = result?.[environment.auth0.appMetadata];
      this.userId = meta?.id;
      const impersonatorId = meta?.impersonator_id;
      this.isImpersonating = impersonatorId && impersonatorId != this.userId;
    });
  }

  unimpersonate() {
    const impersonatorId =
      this.auth0UserData[environment.auth0.appMetadata]?.impersonator_id;
    this.auth0.deleteIsAuthedCookie();
    this.adminService.userImpersonate(impersonatorId, impersonatorId);
  }

  openedNotifications() {
    this.googleAnaylyticsService.eventEmitter(
      this.user.email,
      'Opened Notifications Side Menu',
    );
  }

  openedStats() {
    this.googleAnaylyticsService.eventEmitter(
      this.user.email,
      'Opened Notifications Facility Stats',
    );
  }

  openedSwitchFacility() {
    this.googleAnaylyticsService.eventEmitter(
      this.user.email,
      'Opened Switch Facility',
    );
  }

  closingPopup() {
    this.isProfilePopupOpen = false;
  }

  goTo(destination: string) {
    this.router.navigate([destination]);
  }

  getNavLogo() {
    if (!environment.hydraEnabled || !this.supplierConfig.logo) {
      this.setDefaultLogos();
    } else {
      try {
        const logo = this.supplierConfig.logo as any;
        this.smallLogo = logo.small;
        this.largeLogo = logo.large;
        this.logoAltText = logo.alt;
      } catch {
        this.setDefaultLogos();
      }
    }
  }

  setDefaultLogos() {
    this.largeLogo = '/assets/img/logos/medigi-logo.png';
    this.smallLogo = '/assets/img/logos/medigi-blue-icon.svg';
    this.logoAltText = 'Medigi Logo';
  }

  openChat() {
    if ((window as any)._chatlio) {
      (window as any)._chatlio.open();
    }
  }

  toggleSupportChat() {
    // Navigate to the standalone AI agents surface. Admin-only for now (the
    // button and the /support-chat route are both gated to Administrator).
    // Side-panel code is kept intentionally — it may be re-wired later as a
    // quick-access surface; leave `isSupportChatOpen` untouched.
    this.router.navigate(['/support-chat']);
  }
}

@NgModule({
  imports: [
    CommonModule,
    DxButtonModule,
    UserPanelModule,
    DxToolbarModule,
    CartPanelModule,
    HelpDropdownBtnModule,
    CartSideModule,
    SwitchFacilitiesModule,
    DxTextBoxModule,
    SearchPanelModule,
    DxPopupModule,
    DocumentUploadModule,
    CalibrationFileUploadModule,
    NotificationsModule,
    JobTrackerIndicatorModule,
    FacilityAndUserStatsModule,
    UserProfileModule,
    FinancialStatementsUploaderModule,
    DxTabPanelModule,
    RouterModule,
    AiAgentChatModule,
  ],
  declarations: [HeaderComponent],
  exports: [HeaderComponent],
})
export class HeaderModule {}
